import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [faultBellColor, setFaultBellColor] = useState(null); // 'blue'|'orange'|'red'
  const [wsStatus, setWsStatus] = useState('disconnected');
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const reconnectDelay = useRef(1000);

  // ── Pobierz początkową liczbę nieprzeczytanych wiadomości z API ──────────────
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/messages/conversations');
      const convos = res.data?.conversations || [];
      const total = convos.reduce((sum, c) => sum + (parseInt(c.unread) || 0), 0);
      setUnreadMessages(total);
    } catch (_) {}
  }, [user?.id]);

  // Pobierz nieprzeczytane powiadomienia i ustaw kolor dzwoneczka
  const fetchUnreadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/notifications?limit=50&unread=true');
      const notifs = res.data?.notifications || [];
      setNotifications(notifs);
      setUnread(notifs.filter(n => !n.read).length);

      // Ustaw kolor dzwoneczka wg najważniejszej nieprzeczytanej usterki
      const priority = { red: 3, orange: 2, blue: 1 };
      let topColor = null;
      for (const n of notifs) {
        if (n.read) continue;
        if (n.type === 'fault') {
          try {
            const meta = typeof n.meta === 'string' ? JSON.parse(n.meta) : (n.meta || {});
            const col = meta?.bell_color;
            if (col && (!topColor || (priority[col] || 0) > (priority[topColor] || 0))) {
              topColor = col;
            }
          } catch (_) {}
        }
      }
      if (topColor) setFaultBellColor(topColor);
    } catch (_) {}
  }, [user?.id]);

  // Pobierz przy logowaniu - małe opóźnienie żeby token był dostępny
  useEffect(() => {
    if (user) {
      // Małe opóźnienie dla pewności że token jest w localStorage
      const timer = setTimeout(() => {
        fetchUnreadCount();
        fetchUnreadNotifications();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [user?.id]);

  // ── WebSocket ─────────────────────────────────────────────────────────────────
  const connectWS = useCallback(() => {
    if (!user) return;
    const token = localStorage.getItem('carfleet_token');
    if (!token) return;

    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    const wsUrl = (process.env.REACT_APP_WS_URL || 'ws://localhost:3001') + '/ws';
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus('connected');
      reconnectDelay.current = 1000;
      ws.send(JSON.stringify({ type: 'auth', token }));
      // Odśwież licznik po reconnect
      fetchUnreadCount();
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);

        if (msg.type === 'auth_ok') return;

        if (msg.type === 'presence') {
          setOnlineUsers(prev => {
            const next = new Set(prev);
            if (msg.status === 'online') next.add(msg.userId);
            else next.delete(msg.userId);
            return next;
          });
          return;
        }

        if (msg.type === 'new_notification') {
          setNotifications(prev => [msg.notification, ...prev]);
          setUnread(prev => prev + 1);
          const n = msg.notification;
          // Ustaw kolor dzwoneczka wg usterki (priorytet: red > orange > blue)
          if (n?.type === 'fault') {
            try {
              const meta = typeof n.meta === 'string' ? JSON.parse(n.meta) : (n.meta || {});
              const newColor = meta?.bell_color;
              if (newColor) {
                const priority = { red: 3, orange: 2, blue: 1 };
                setFaultBellColor(prev => {
                  if (!prev || (priority[newColor] || 0) > (priority[prev] || 0)) return newColor;
                  return prev;
                });
              }
            } catch (_) {}
          }
          if (n) {
            const icon = {
              mileage: '🚗', document: '📄', fault: '⚠️',
              service: '🔧', insurance: '🛡️', inspection: '🔩', medical: '🏥',
            }[n.type] || '🔔';
            toast.info(`${icon} ${n.title}`, {
              position: 'top-right',
              autoClose: 5000,
              onClick: () => { window.location.href = '/notifications'; },
            });
          }
          return;
        }

        if (msg.type === 'new_message') {
          // Ignoruj własne wiadomości
          if (msg.sender_id && user && Number(msg.sender_id) === Number(user.id)) return;

          setUnreadMessages(prev => prev + 1);

          const sender = msg.sender_name || 'Nowa wiadomość';
          const preview = msg.body
            ? (msg.body.length > 60 ? msg.body.slice(0, 60) + '…' : msg.body)
            : '';

          toast.info(
            React.createElement('div', null,
              React.createElement('div', { className: 'font-semibold text-sm' }, '💬 ' + sender),
              preview && React.createElement('div', { className: 'text-xs opacity-80 mt-0.5' }, preview)
            ),
            {
              position: 'top-right',
              autoClose: 6000,
              onClick: () => { window.location.href = '/messages'; },
            }
          );
          return;
        }
      } catch (_) {}
    };

    ws.onclose = () => {
      setWsStatus('disconnected');
      const delay = Math.min(reconnectDelay.current, 30000);
      reconnectDelay.current = Math.min(delay * 2, 30000);
      reconnectRef.current = setTimeout(connectWS, delay);
    };

    ws.onerror = () => {};
  }, [user?.id, fetchUnreadCount]);

  useEffect(() => {
    if (user) connectWS();
    return () => {
      clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [user?.id]);

  const sendWS = (data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  };

  const markRead = (id) => {
    setNotifications(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, read: true } : n);
      // Sprawdź czy są jeszcze nieprzeczytane usterki - aktualizuj kolor
      const priority = { red: 3, orange: 2, blue: 1 };
      let topColor = null;
      for (const n of updated) {
        if (n.read || n.type !== 'fault') continue;
        try {
          const meta = typeof n.meta === 'string' ? JSON.parse(n.meta) : (n.meta || {});
          const col = meta?.bell_color;
          if (col && (!topColor || (priority[col] || 0) > (priority[topColor] || 0))) topColor = col;
        } catch (_) {}
      }
      setFaultBellColor(topColor);
      return updated;
    });
    setUnread(prev => Math.max(0, prev - 1));
  };

  // Wyczyść badge i odśwież z API
  const clearMessageBadge = useCallback(() => {
    setUnreadMessages(0);
    // Odśwież po chwili żeby zsynchronizować z API
    setTimeout(fetchUnreadCount, 1000);
  }, [fetchUnreadCount]);

  return (
    <NotificationsContext.Provider value={{
      notifications, unread, unreadMessages,
      wsStatus, onlineUsers, faultBellColor,
      sendWS, markRead, setNotifications, setUnread,
      clearMessageBadge, fetchUnreadCount, fetchUnreadNotifications,
      resetFaultBellColor: () => setFaultBellColor(null),
    }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationsContext);
